import React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider } from '@tanstack/react-router'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider, type Query } from '@tanstack/react-query'
import { ApiError, isSessionError } from './lib/api'
import { queryKeys } from './lib/queries'
import { createAppRouter } from './router'
import './styles/main.css'

/* Spec COG-005 — entry of the Painel da TI screens. */

/* Session expired on any call: re-check GET /me, which then shows the login card.
   (GET /me itself is skipped, or its own 401 would re-trigger it forever.) */
function onSessionError(error: unknown, query?: Query<unknown, unknown, unknown, readonly unknown[]>) {
    if (!isSessionError(error)) return
    if (query && query.queryKey[1] === queryKeys.me[1]) return
    void queryClient.invalidateQueries({ queryKey: queryKeys.me })
}

const queryClient = new QueryClient({
    queryCache: new QueryCache({ onError: onSessionError }),
    mutationCache: new MutationCache({ onError: error => onSessionError(error) }),
    defaultOptions: {
        queries: {
            staleTime: 5_000,
            refetchOnWindowFocus: true,
            // Client errors (4xx) do not get better by repeating; network and 5xx try once more.
            retry: (failureCount, error) => {
                if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
                return failureCount < 1
            },
        },
        mutations: { retry: false },
    },
})

const router = createAppRouter(queryClient)

ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
        </QueryClientProvider>
    </React.StrictMode>,
)
